/**
 * TicketDetailSidebar — the right-hand properties panel on the ticket detail view.
 * Receives the selected ticket and a bag of handler callbacks from the parent page.
 */
import React from 'react';
import { X, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  StatusBadge,
  PriorityBadge,
  SlaBadge,
  formatDateTime,
} from './TicketComponents';
import {
  internalStatusBadgeClasses,
  DUE_DATE_PRESETS,
  formatDueDate,
  dueDateRelative,
} from '../../lib/ticketFields';

const TICKET_TYPE_LABELS = {
  standard_support: 'Standard Support',
  product_cross_module: 'Product & Cross Module',
  new_scope: 'New Scope',
};

export function TicketDetailSidebar({
  selectedTicket,
  isStaff,
  assignees = [],
  availableModulesForEdit = [],
  internalStatuses = [],
  onClose,
  onUpdateStatus,
  onUpdatePriority,
  onUpdateModules,
  onUpdateTicketType,
  onAssignTicket,
  onOpenAllAttachments,
  onUpdateInternalStatus,
  onUpdateDueDate,
  onUpdateFollowers,
}) {
  if (!selectedTicket) return null;

  const staff = typeof isStaff === 'function' ? isStaff() : !!isStaff;
  const currentInternal = internalStatuses.find((i) => i.id === selectedTicket.internal_status);
  const totalAtt =
    (selectedTicket.attachments?.length || 0) +
    (selectedTicket.replies || []).reduce(
      (n, r) => n + (r.attachments?.length || 0),
      0
    );
  const followers = Array.isArray(selectedTicket.followers) ? selectedTicket.followers : [];
  const toggleFollower = (assignee) => {
    if (!onUpdateFollowers) return;
    const exists = followers.find((f) => (f.user_id && f.user_id === assignee.id) || (f.email && f.email === assignee.email));
    let next;
    if (exists) {
      next = followers.filter((f) => f !== exists);
    } else {
      next = [
        ...followers,
        { user_id: assignee.id || '', name: assignee.name || assignee.email || '', email: assignee.email || '' },
      ];
    }
    onUpdateFollowers(next);
  };
  const removeFollower = (f) => {
    if (!onUpdateFollowers) return;
    onUpdateFollowers(followers.filter((x) => x !== f));
  };

  return (
    <div className="w-80 bg-white border-l border-slate-200 flex flex-col h-full min-h-0" data-testid="ticket-detail-sidebar">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/></svg>
          </div>
          <h3 className="font-semibold text-slate-900 text-sm">Ticket Details</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="ticket-detail-close">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 space-y-3" data-testid="ticket-detail-scroll">
        {/* Contact Info */}
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="px-3 py-2 border-b border-slate-200 text-xs font-medium text-slate-600">
            Contact Info
          </div>
          <div className="p-3 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-500">Submitter</span>
              <span className="text-slate-800 font-medium">{selectedTicket.created_by_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Email</span>
              <span className="text-slate-700 text-xs">{selectedTicket.created_by_email || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Reported By</span>
              <span className="text-slate-800">{selectedTicket.reported_person_name || '-'}</span>
            </div>
          </div>
        </div>

        {/* Ticket Info */}
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="px-3 py-2 border-b border-slate-200 text-xs font-medium text-slate-600">
            Ticket Information
          </div>
          <div className="p-3 text-sm space-y-3">
            {staff ? (
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={selectedTicket.status} onValueChange={onUpdateStatus}>
                  <SelectTrigger className="mt-1" data-testid="details-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                    <SelectItem value="under_investigation">Under Investigation</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Status</span>
                <StatusBadge status={selectedTicket.status} />
              </div>
            )}

            {staff ? (
              <div>
                <Label className="text-xs text-slate-500">Priority</Label>
                <Select value={selectedTicket.priority} onValueChange={onUpdatePriority}>
                  <SelectTrigger className="mt-1" data-testid="details-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Priority</span>
                <PriorityBadge priority={selectedTicket.priority} />
              </div>
            )}

            {staff ? (
              <div>
                <Label className="text-xs text-slate-500">Modules</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-1 w-full justify-between h-auto min-h-9 py-1.5 px-3 font-normal"
                      data-testid="details-modules-multiselect"
                    >
                      <div className="flex flex-wrap gap-1 items-center max-w-[calc(100%-1rem)]">
                        {selectedTicket.modules && selectedTicket.modules.length > 0 ? (
                          selectedTicket.modules.map((m) => (
                            <Badge
                              key={m}
                              variant="outline"
                              className="bg-purple-50 text-purple-700 border-purple-200 text-[11px]"
                            >
                              {m}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-slate-400 text-sm">Select modules…</span>
                        )}
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 rotate-90 flex-shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <div className="px-3 py-2 border-b border-slate-100 text-xs font-medium text-slate-600">
                      Select modules
                    </div>
                    <div className="max-h-60 overflow-auto p-1">
                      {availableModulesForEdit.map((m) => {
                        const current =
                          selectedTicket.modules ||
                          (selectedTicket.module ? [selectedTicket.module] : []);
                        const checked = current.includes(m);
                        return (
                          <label
                            key={m}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                const nextArr = v
                                  ? [...current.filter((x) => x !== m), m]
                                  : current.filter((x) => x !== m);
                                onUpdateModules?.(nextArr);
                              }}
                              data-testid={`details-module-opt-${m}`}
                            />
                            <span className="text-sm text-slate-700">{m}</span>
                          </label>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Module</span>
                <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                  {(selectedTicket.modules && selectedTicket.modules.length > 0
                    ? selectedTicket.modules
                    : [selectedTicket.module]
                  )
                    .filter(Boolean)
                    .map((m) => (
                      <Badge
                        key={m}
                        variant="outline"
                        className="bg-purple-50 text-purple-700 border-purple-200 text-[11px]"
                      >
                        {m}
                      </Badge>
                    ))}
                </div>
              </div>
            )}

            {/* Ticket Type (staff-only edit; visible to everyone) */}
            {staff ? (
              <div>
                <Label className="text-xs text-slate-500">Ticket Type</Label>
                <Select
                  value={selectedTicket.ticket_type || 'standard_support'}
                  onValueChange={onUpdateTicketType}
                >
                  <SelectTrigger className="mt-1" data-testid="details-ticket-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard_support">Standard Support Ticket</SelectItem>
                    <SelectItem value="product_cross_module">Product and Cross Module Ticket</SelectItem>
                    <SelectItem value="new_scope">New Scope</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Ticket Type</span>
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                  {TICKET_TYPE_LABELS[selectedTicket.ticket_type || 'standard_support']}
                </Badge>
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-slate-500">Location</span>
              <span className="text-slate-800 text-xs">{selectedTicket.location_name}</span>
            </div>

            {selectedTicket.sla_hours && (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">SLA</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-700">{selectedTicket.sla_hours}h</span>
                  <SlaBadge
                    breached={selectedTicket.sla_breached}
                    paused={selectedTicket.sla_paused}
                    dueAt={selectedTicket.sla_due_at}
                    hours={selectedTicket.sla_hours}
                  />
                </div>
              </div>
            )}

            {staff && (
              <div>
                <Label className="text-xs text-slate-500">Assignee</Label>
                <Select
                  value={selectedTicket.assigned_to_id || 'unassigned'}
                  onValueChange={(v) => onAssignTicket?.(v === 'unassigned' ? '' : v)}
                >
                  <SelectTrigger className="mt-1" data-testid="ticket-assignee-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {assignees.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name || a.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Followers — anyone can add/remove (customers can add teammates too). */}
            {onUpdateFollowers ? (
              <div>
                <Label className="text-xs text-slate-500">Followers</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-1 w-full justify-between h-auto min-h-9 py-1.5 px-3 font-normal"
                      data-testid="details-followers-multiselect"
                    >
                      <div className="flex flex-wrap gap-1 items-center max-w-[calc(100%-1rem)]">
                        {followers.length > 0 ? (
                          followers.map((f) => (
                            <Badge
                              key={(f.user_id || '') + (f.email || '')}
                              variant="outline"
                              className="bg-cyan-50 text-cyan-700 border-cyan-200 text-[11px] gap-1"
                            >
                              {f.name || f.email}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); removeFollower(f); }}
                                className="ml-1 text-cyan-700/70 hover:text-cyan-900"
                                aria-label={`Remove ${f.email}`}
                                data-testid={`remove-follower-${f.email}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))
                        ) : (
                          <span className="text-slate-400 text-sm">Add followers…</span>
                        )}
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 rotate-90 flex-shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <div className="px-3 py-2 border-b border-slate-100 text-xs font-medium text-slate-600">
                      Select users to copy on all replies
                    </div>
                    <div className="max-h-60 overflow-auto p-1">
                      {assignees.length === 0 && (
                        <div className="px-2 py-3 text-xs text-slate-400 text-center">No users available</div>
                      )}
                      {assignees.map((a) => {
                        const checked = followers.some((f) => (f.user_id && f.user_id === a.id) || (f.email && f.email === a.email));
                        return (
                          <label
                            key={a.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleFollower(a)}
                              data-testid={`details-follower-opt-${a.id}`}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-slate-700 truncate">{a.name || a.email}</div>
                              {a.name && a.email && (
                                <div className="text-[11px] text-slate-400 truncate">{a.email}</div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
                <div className="text-[11px] text-slate-500 mt-1">Followers are CC'd on every email reply.</div>
              </div>
            ) : followers.length > 0 ? (
              <div className="flex justify-between items-start">
                <span className="text-slate-500">Followers</span>
                <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                  {followers.map((f) => (
                    <Badge
                      key={(f.user_id || '') + (f.email || '')}
                      variant="outline"
                      className="bg-cyan-50 text-cyan-700 border-cyan-200 text-[11px]"
                    >
                      {f.name || f.email}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Internal status — editable by staff, visible to customer */}
            {staff ? (
              <div>
                <Label className="text-xs text-slate-500">Internal Status</Label>
                <Select
                  value={selectedTicket.internal_status || 'none'}
                  onValueChange={(v) => onUpdateInternalStatus?.(v === 'none' ? null : v)}
                >
                  <SelectTrigger className="mt-1" data-testid="ticket-internal-status-select">
                    <SelectValue placeholder="Not set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {internalStatuses.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : currentInternal ? (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Internal Status</span>
                <Badge
                  variant="outline"
                  className={`${internalStatusBadgeClasses(currentInternal.color)} text-xs`}
                  data-testid="ticket-internal-status-badge"
                >
                  {currentInternal.label}
                </Badge>
              </div>
            ) : null}

            {/* Due Date — editable by staff via presets or custom datetime, visible to customer */}
            {staff ? (
              <div>
                <Label className="text-xs text-slate-500">Delivery / Due Date</Label>
                <Select
                  value=""
                  onValueChange={(presetKey) => {
                    const preset = DUE_DATE_PRESETS.find((p) => p.key === presetKey);
                    if (!preset) return;
                    onUpdateDueDate?.(preset.compute());
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="ticket-due-date-preset">
                    <SelectValue placeholder="Set with preset…" />
                  </SelectTrigger>
                  <SelectContent>
                    {DUE_DATE_PRESETS.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-1.5 flex items-center gap-2">
                  <input
                    type="datetime-local"
                    value={
                      selectedTicket.due_date
                        ? new Date(selectedTicket.due_date).toISOString().slice(0, 16)
                        : ''
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      onUpdateDueDate?.(v ? new Date(v).toISOString() : null);
                    }}
                    className="flex-1 h-8 rounded-md border border-slate-200 px-2 text-sm"
                    data-testid="ticket-due-date-input"
                  />
                </div>
                <div className="text-[11px] text-slate-500 mt-1" data-testid="ticket-due-date-human">
                  {selectedTicket.due_date
                    ? `Due ${formatDueDate(selectedTicket.due_date)} · ${dueDateRelative(selectedTicket.due_date)}`
                    : 'No due date set'}
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-start">
                <span className="text-slate-500">Delivery date</span>
                <div className="text-right" data-testid="ticket-due-date-readonly">
                  <div className="text-slate-800 text-xs">{formatDueDate(selectedTicket.due_date)}</div>
                  {selectedTicket.due_date && (
                    <div className="text-[11px] text-slate-500">{dueDateRelative(selectedTicket.due_date)}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Timestamps */}
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="px-3 py-2 border-b border-slate-200 text-xs font-medium text-slate-600">
            Timeline
          </div>
          <div className="p-3 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-500">Created</span>
              <span className="text-slate-800 text-xs">{formatDateTime(selectedTicket.created_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Updated</span>
              <span className="text-slate-800 text-xs">{formatDateTime(selectedTicket.updated_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Replies</span>
              <span className="text-slate-800">{selectedTicket.replies?.length || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Attachments</span>
              {totalAtt > 0 ? (
                <button
                  type="button"
                  onClick={onOpenAllAttachments}
                  className="text-indigo-600 hover:text-indigo-700 hover:underline text-xs font-medium"
                  data-testid="open-all-attachments"
                >
                  View all ({totalAtt})
                </button>
              ) : (
                <span className="text-slate-800">0</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicketDetailSidebar;
